import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Building2, User, FileText, Upload, Download, Trash2, Save, Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = [
  { key: 'plan_labo', label: 'Plan du Laboratoire' },
  { key: 'certificat_bpf', label: 'Certificat BPF' },
  { key: 'depot_inpi', label: 'Dépôt de marque INPI' },
  { key: 'autre', label: 'Autre document' },
];

const LabProfile = () => {
  const [settings, setSettings] = useState({
    company_name: '', siret: '', address: '', phone: '', email: '',
    responsible_person: '', ansm_number: '',
    documents: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await fetch(`${API_BASE}/api/lab-settings?user_id=${user.id || 'demo-user'}`);
      const data = await res.json();
      setSettings({
        company_name: data.company_name || '',
        siret: data.siret || '',
        address: data.address || data.lab_address || '',
        phone: data.phone || '',
        email: data.email || '',
        responsible_person: data.responsible_person || '',
        ansm_number: data.ansm_number || '',
        documents: data.documents || []
      });
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await fetch(`${API_BASE}/api/lab-settings?user_id=${user.id || 'demo-user'}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: settings.company_name,
          lab_name: settings.company_name,
          siret: settings.siret,
          address: settings.address,
          lab_address: settings.address,
          phone: settings.phone,
          email: settings.email,
          responsible_person: settings.responsible_person,
          ansm_number: settings.ansm_number,
        })
      });
      if (res.ok) toast.success('Profil sauvegardé');
      else toast.error('Erreur de sauvegarde');
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const handleUpload = async (docType, file) => {
    setUploading(docType);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_type', docType);
      const res = await fetch(`${API_BASE}/api/lab-settings/documents/upload?user_id=${user.id || 'demo-user'}`, {
        method: 'POST', body: formData
      });
      if (res.ok) { toast.success('Document uploadé'); loadSettings(); }
      else toast.error('Erreur upload');
    } catch (e) { toast.error('Erreur'); }
    finally { setUploading(null); }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await fetch(`${API_BASE}/api/lab-settings/documents/${docId}?user_id=${user.id || 'demo-user'}`, { method: 'DELETE' });
      toast.success('Document supprimé');
      loadSettings();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleDownloadDoc = async (docId, filename) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await fetch(`${API_BASE}/api/lab-settings/documents/${docId}/download?user_id=${user.id || 'demo-user'}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Erreur téléchargement'); }
  };

  if (loading) return <div className="p-8"><div className="skeleton-shimmer h-64 rounded-lg"></div></div>;

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-4xl font-semibold text-foreground mb-2 tracking-tight">Profil Labo</h1>
        <p className="text-base text-muted-foreground">Configuration du laboratoire — ces informations sont injectées automatiquement dans vos DIP et fiches de fabrication</p>
      </div>

      {/* Infos Légales */}
      <Card className="mb-6" data-testid="card-legal-info">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Building2 size={20} /> Informations Légales</CardTitle>
          <CardDescription>Identité de votre entreprise</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Nom de l'entreprise</label>
              <Input data-testid="input-company-name" value={settings.company_name} onChange={(e) => setSettings({...settings, company_name: e.target.value})} placeholder="Mon Laboratoire Cosmétique" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">SIRET</label>
              <Input data-testid="input-siret" value={settings.siret} onChange={(e) => setSettings({...settings, siret: e.target.value})} placeholder="123 456 789 00012" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Adresse</label>
            <Input data-testid="input-address" value={settings.address} onChange={(e) => setSettings({...settings, address: e.target.value})} placeholder="12 Rue des Cosmétiques, 75001 Paris" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Téléphone</label>
              <Input data-testid="input-phone" value={settings.phone} onChange={(e) => setSettings({...settings, phone: e.target.value})} placeholder="01 23 45 67 89" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Email</label>
              <Input data-testid="input-email" type="email" value={settings.email} onChange={(e) => setSettings({...settings, email: e.target.value})} placeholder="contact@monlabo.fr" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Réglementaire */}
      <Card className="mb-6" data-testid="card-regulatory">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Shield size={20} /> Réglementaire</CardTitle>
          <CardDescription>Informations réglementaires pour vos DIP</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Personne Responsable</label>
              <Input data-testid="input-responsible" value={settings.responsible_person} onChange={(e) => setSettings({...settings, responsible_person: e.target.value})} placeholder="Prénom Nom" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">N° Déclaration ANSM</label>
              <Input data-testid="input-ansm" value={settings.ansm_number} onChange={(e) => setSettings({...settings, ansm_number: e.target.value})} placeholder="ANSM-XXXXX" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents Labo */}
      <Card className="mb-6" data-testid="card-lab-documents">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><FileText size={20} /> Documents du Laboratoire</CardTitle>
          <CardDescription>Certifications et documents officiels</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {DOC_TYPES.map(dt => {
            const existingDocs = settings.documents.filter(d => d.doc_type === dt.key);
            return (
              <div key={dt.key} className="border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium">{dt.label}</p>
                  <label className="cursor-pointer">
                    <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                      onChange={(e) => { if (e.target.files[0]) handleUpload(dt.key, e.target.files[0]); e.target.value=''; }} />
                    <Button size="sm" variant="outline" className="gap-1.5 text-xs" asChild disabled={uploading === dt.key}>
                      <span><Upload size={13} /> {uploading === dt.key ? '...' : 'Ajouter'}</span>
                    </Button>
                  </label>
                </div>
                {existingDocs.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Aucun document</p>
                ) : (
                  <div className="space-y-1.5">
                    {existingDocs.map(doc => (
                      <div key={doc.id} className="flex items-center gap-2 text-xs bg-muted/50 rounded px-3 py-2">
                        <FileText size={14} className="text-primary shrink-0" />
                        <span className="truncate flex-1">{doc.filename}</span>
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => handleDownloadDoc(doc.id, doc.filename)}><Download size={13} /></Button>
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive" onClick={() => handleDeleteDoc(doc.id)}><Trash2 size={13} /></Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Button data-testid="btn-save-lab" className="w-full gap-2 h-12" onClick={handleSave} disabled={saving}>
        {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
        {saving ? 'Sauvegarde...' : 'Sauvegarder le profil'}
      </Button>

      {/* Legal info */}
      <Card className="mt-8 border-gray-200">
        <CardHeader>
          <CardTitle className="text-base">Informations légales</CardTitle>
          <CardDescription>Documents juridiques de DIP PILOTE +IA</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <a href="/legal/mentions" target="_blank" rel="noopener noreferrer"
             data-testid="profile-link-mentions"
             className="flex items-center gap-3 p-3 rounded-md border border-border hover:border-[#205F79]/40 hover:bg-muted/40 transition-all">
            <FileText size={18} className="text-[#205F79]" />
            <div className="min-w-0">
              <p className="text-sm font-medium">Mentions légales</p>
              <p className="text-xs text-muted-foreground">Éditeur, hébergeur</p>
            </div>
          </a>
          <a href="/legal/cgu" target="_blank" rel="noopener noreferrer"
             data-testid="profile-link-cgu"
             className="flex items-center gap-3 p-3 rounded-md border border-border hover:border-[#205F79]/40 hover:bg-muted/40 transition-all">
            <FileText size={18} className="text-[#205F79]" />
            <div className="min-w-0">
              <p className="text-sm font-medium">CGU / CGV</p>
              <p className="text-xs text-muted-foreground">Conditions générales</p>
            </div>
          </a>
          <a href="/legal/confidentialite" target="_blank" rel="noopener noreferrer"
             data-testid="profile-link-rgpd"
             className="flex items-center gap-3 p-3 rounded-md border border-border hover:border-[#205F79]/40 hover:bg-muted/40 transition-all">
            <FileText size={18} className="text-[#205F79]" />
            <div className="min-w-0">
              <p className="text-sm font-medium">Confidentialité (RGPD)</p>
              <p className="text-xs text-muted-foreground">Vos données personnelles</p>
            </div>
          </a>
        </CardContent>
      </Card>
    </div>
  );
};

export default LabProfile;
